import { TextField, Typography } from "@mui/material";
import { add, intervalToDuration } from "date-fns";
import { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { ValidationError } from "./orderTypes";

// FIXME could this take in more general time value?
type Props = {
  id: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
  validationErrors: ValidationError[];
};

// FIXME: should this be used?
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type Values = {
  formattedValue: string;
  value: string;
  floatValue: number | undefined;
};

type Duration = { value: string; isNextDay: boolean };

type InputProps = {
  duration: Duration;
  ref: any;
  // rest of the props
  [x: string]: any;
};

const Input = ({ value, duration, error, ...rest }: InputProps) => (
  <TextField
    autoComplete="off"
    fullWidth
    size="small"
    placeholder="hh:mm - hh:mm"
    sx={{ backgroundColor: "rgba(249, 249, 250, 0.6)" }}
    value={value}
    error={error}
    InputProps={{
      endAdornment: (
        <span>
          <Typography
            sx={{
              color: "rgba(0, 0, 0, 0.38)",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {duration.value}
          </Typography>
        </span>
      ),
    }}
    {...rest}
  />
);

/// From start date and time fields as a string, parse to
/// start and end date times as number objects representing
/// epoch timestamps.
export function parseToInterval(value: string, today: Date) {
  const startTime = value.slice(0, 4);
  const endTime = value.slice(4, 9);

  const tomorrow = add(today, {
    days: 1,
  });

  const isNextDay = startTime > endTime;
  const endDate = isNextDay ? tomorrow : today;

  const startDateTime = today.setHours(
    +startTime.slice(0, 2),
    +startTime.slice(2, 4),
    0,
    0,
  );
  const endDateTime = endDate.setHours(
    +endTime.slice(0, 2),
    +endTime.slice(2, 4),
    0,
    0,
  );

  return { isNextDay, startDateTime, endDateTime };
}

export const getDuration = (value: string) => {
  const today = new Date();
  const { isNextDay, startDateTime, endDateTime } = parseToInterval(
    value,
    today,
  );

  if (startDateTime && endDateTime) {
    const duration = intervalToDuration({
      start: new Date(startDateTime),
      end: new Date(endDateTime),
    });

    const hours = duration.hours?.toString().padStart(2, "0") || "00";
    const minutes = duration.minutes?.toString().padStart(2, "0") || "00";

    return {
      value: `${hours}:${minutes}`,
      isNextDay,
    };
  }

  return { value: "00:00", isNextDay: false };
};

const OrderTimeField = ({
  id,
  onChange,
  value,
  disabled,
  validationErrors,
}: Props) => {
  const [duration, setDuration] = useState({ value: "", isNextDay: false });

  const error = validationErrors.find(
    (error) => error.id === id && error.field === "time",
  );

  useEffect(() => {
    if (!value) return;

    if (duration.value && value.length !== 8) {
      return setDuration({ value: "", isNextDay: false });
    }
    const durationObject: Duration = getDuration(value);

    if (value.length === 8 && durationObject.value !== duration.value) {
      return setDuration(durationObject);
    }
  }, [value, duration.value]);

  // allowed only when spreaded
  const inputProps = { duration, error: !!error };

  return (
    <PatternFormat
      {...inputProps}
      disabled={disabled}
      value={value}
      valueIsNumericString
      format="##:## - ##:##"
      customInput={Input}
      onValueChange={(values) => onChange(values.value)}
      mask="_"
    />
  );
};

export default OrderTimeField;
